import React, { createContext, useState, useContext, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { applyDevPreview } from '../devRolePreview';

const AuthContext = createContext();

// Decode JWT payload without a library (base64url → JSON)
const decodeTokenPayload = (token) => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

// Check if a token is expired (with 30s buffer to avoid edge cases)
const isTokenExpired = (token) => {
  const payload = decodeTokenPayload(token);
  if (!payload?.exp) return true;
  return Date.now() >= (payload.exp * 1000) - 30000;
};

// ─── Cross-tab refresh coordination ───
// Prevents multiple tabs from calling /api/auth/refresh simultaneously,
// which would trigger token reuse detection and revoke the entire session.
const REFRESH_LOCK_KEY = 'clara_refresh_lock';
const REFRESH_LOCK_TTL = 10000; // 10s max — prevents stale locks from crashed tabs
const REFRESH_CHANNEL_NAME = 'clara_auth_refresh';

// Unique ID for this tab — prevents the same-millisecond lock collision
// where two tabs both write Date.now() and both think they won.
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// BroadcastChannel for instant cross-tab messaging (supported in all modern browsers)
let refreshChannel = null;
try {
  refreshChannel = new BroadcastChannel(REFRESH_CHANNEL_NAME);
} catch {
  // BroadcastChannel not supported — localStorage storage event is the fallback
}

// Lock via localStorage with unique tab ID — prevents same-millisecond race.
// Format stored: "timestamp:tabId" so we can check both freshness and ownership.
const acquireRefreshLock = () => {
  const now = Date.now();
  const existing = localStorage.getItem(REFRESH_LOCK_KEY);
  if (existing) {
    const lockTime = parseInt(existing.split(':')[0], 10);
    if (now - lockTime < REFRESH_LOCK_TTL) {
      return false; // Another tab holds a valid lock
    }
    // Lock expired (tab crashed or took too long) — steal it
  }
  const lockValue = `${now}:${TAB_ID}`;
  localStorage.setItem(REFRESH_LOCK_KEY, lockValue);
  // Read back to verify we actually won (CAS-like check).
  // Because lockValue includes a unique TAB_ID, two tabs can never
  // write the same value, so exactly one will match on read-back.
  return localStorage.getItem(REFRESH_LOCK_KEY) === lockValue;
};

const releaseRefreshLock = () => {
  localStorage.removeItem(REFRESH_LOCK_KEY);
};

// Broadcast a new token to all other tabs
const broadcastNewToken = (newToken) => {
  if (refreshChannel) {
    refreshChannel.postMessage({ type: 'TOKEN_REFRESHED', token: newToken });
  }
  // Also write to localStorage — the `storage` event fires in OTHER tabs
  // (not in the tab that wrote it), so this serves as a fallback channel.
  localStorage.setItem('token', newToken);
};

// Broadcast logout to all other tabs
const broadcastLogout = () => {
  if (refreshChannel) {
    refreshChannel.postMessage({ type: 'LOGOUT' });
  }
};

// Fired synchronously BEFORE the token is removed from localStorage on any
// logout path. Open editors (the ticket dialog) listen for it and flush their
// draft via sendBeacon — it is their only chance to do so while the token is
// still readable; the redirect's beforeunload fires after removal, when
// saveViaBeacon can no longer authenticate.
const SESSION_ENDING_EVENT = 'clara:session-ending';
const emitSessionEnding = () => {
  try {
    window.dispatchEvent(new Event(SESSION_ENDING_EVENT));
  } catch {
    // never let draft-flush plumbing break the logout itself
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const isRefreshingRef = useRef(false);
  const failedQueueRef = useRef([]);
  const isRedirectingRef = useRef(false); // Prevent multiple redirects

  // qaTeamEnforcementActive rides only on GET /api/auth/profile (the QA team
  // switcher is gated on it). Other endpoints that replace the whole user
  // object — PUT /api/auth/profile — omit it, so carry the known value over
  // instead of letting it read as "off" mid-session.
  const setUser = (next) => {
    setUserState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      // Rollback self-heal: with enforcement back to off/shadow the team switcher
      // AND the acting-as chip are both hidden, so a selection left over from an
      // enforced session would keep scoping this tab with nothing to show it and
      // no way to undo it. Every profile payload that reports enforcement off
      // drops the key (ACTING_TEAM_KEY in constants/qaTeams.js; literal here to
      // keep AuthContext free of qa-manager deps), so login and any later profile
      // fetch both heal the tab. Tested on the RESOLVED value, not the raw
      // argument, so a setUser(prev => ...) updater can't skip it; removeItem is
      // idempotent, so StrictMode's double-invoked updater is harmless.
      if (resolved && typeof resolved === 'object' && resolved.qaTeamEnforcementActive === false) {
        try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
      }
      if (!resolved || typeof resolved !== 'object') return resolved;
      if ('qaTeamEnforcementActive' in resolved) return resolved;
      if (prev?.qaTeamEnforcementActive === undefined) return resolved;
      return { ...resolved, qaTeamEnforcementActive: prev.qaTeamEnforcementActive };
    });
  };

  // Define logout function FIRST (before useEffects that use it)
  const performLogout = async (forceRedirect = false) => {
    console.log('🚪 performLogout called, forceRedirect:', forceRedirect);
    emitSessionEnding();

    // If force redirect, skip backend call and just clear everything immediately
    if (forceRedirect) {
      console.log('🔄 Force redirecting to /login...');
      broadcastLogout();
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      // Clear the admin acting-team (ACTING_TEAM_KEY in constants/qaTeams.js;
      // literal here to keep AuthContext free of qa-manager deps) so a stale
      // 'kick'/'hosts' scope can't 403 the next user's QA requests in this tab.
      try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/login';
      return; // Don't continue, page will reload
    }

    // Normal logout - try to call backend
    try {
      await axios.post(
        `${API_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      broadcastLogout();
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      // Clear the admin acting-team (ACTING_TEAM_KEY in constants/qaTeams.js) so
      // a stale 'kick'/'hosts' scope can't 403 the next user in this tab.
      try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
      setToken(null);
      setUser(null);
      setLoading(false);
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/profile`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      // Any error during profile fetch = invalid session, force redirect
      // But only if not already redirecting (interceptor might handle it)
      if (!isRedirectingRef.current) {
        console.log('🚪 Profile fetch failed, clearing session and redirecting to login...');
        isRedirectingRef.current = true;
        emitSessionEnding();
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        // Clear the admin acting-team (ACTING_TEAM_KEY in constants/qaTeams.js) so
        // a stale 'kick'/'hosts' scope can't 403 the next user in this tab.
        try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
        setToken(null);
        setUser(null);
        setLoading(false);
        delete axios.defaults.headers.common['Authorization'];
        window.location.href = '/login';
      }
    }
  };

  // Set up axios interceptors for token refresh
  useEffect(() => {
    // Request interceptor - add token to requests
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        // Always include credentials for cookies (refresh token)
        config.withCredentials = true;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle token refresh on 401
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = originalRequest?.url || '';

        // IMPORTANT: Skip interceptor for refresh and logout endpoints to prevent loops
        const isAuthEndpoint = requestUrl.includes('/api/auth/refresh') ||
                               requestUrl.includes('/api/auth/logout') ||
                               requestUrl.includes('/api/auth/login');

        // If error is 401 and we haven't tried refreshing yet
        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
          if (isRefreshingRef.current) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueueRef.current.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshingRef.current = true;

          try {
            // Use the cross-tab-safe proactiveRefresh instead of calling /refresh directly.
            // This ensures only one tab hits the backend, preventing reuse detection.
            const result = await proactiveRefresh();

            if (result !== true) {
              const refreshFailure = new Error('Refresh failed');
              // 'transient' = rate limit / server blip / network — the refresh
              // cookie is still valid, so the session must NOT be destroyed.
              refreshFailure.transient = result === 'transient';
              throw refreshFailure;
            }

            const newToken = localStorage.getItem('token');

            // Process queued requests
            failedQueueRef.current.forEach((prom) => {
              prom.resolve(newToken);
            });
            failedQueueRef.current = [];

            // Retry original request with new token
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user and force redirect to login
            console.log('🚪 Token refresh failed, forcing redirect to login...');
            failedQueueRef.current.forEach((prom) => {
              prom.reject(refreshError);
            });
            failedQueueRef.current = [];

            // Prevent multiple redirects. Transient refresh failures (429 from
            // the shared-office-IP rate limiter, 5xx, network blips) keep the
            // session alive — the request fails, but a later refresh recovers.
            if (!refreshError.transient && !isRedirectingRef.current) {
              isRedirectingRef.current = true;
              emitSessionEnding();
              localStorage.removeItem('token');
              localStorage.removeItem('userId');
              delete axios.defaults.headers.common['Authorization'];
              window.location.href = '/login';
            }
            return Promise.reject(refreshError);
          } finally {
            isRefreshingRef.current = false;
          }
        }

        // Handle 401 on auth endpoints (refresh failed) - redirect to login
        if (error.response?.status === 401 && isAuthEndpoint && !isRedirectingRef.current) {
          console.log('🚪 Auth endpoint returned 401, redirecting to login...');
          isRedirectingRef.current = true;
          emitSessionEnding();
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          delete axios.defaults.headers.common['Authorization'];
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactively refresh the access token before it expires.
  // Uses cross-tab lock so only ONE tab calls the backend; others wait for the result.
  const proactiveRefresh = async () => {
    // Try to become the tab that does the actual refresh
    if (!acquireRefreshLock()) {
      // Another tab is already refreshing — wait for it to broadcast the new token.
      // We'll pick it up via the BroadcastChannel / storage listener.
      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          // Safety: if we never hear back within 8s, check localStorage directly
          const freshToken = localStorage.getItem('token');
          if (freshToken && !isTokenExpired(freshToken)) {
            setToken(freshToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${freshToken}`;
            resolve(true);
          } else {
            resolve(false);
          }
        }, 8000);

        // Listen for the broadcast from the tab that's refreshing
        const onMessage = (event) => {
          if (event.data?.type === 'TOKEN_REFRESHED' && event.data.token) {
            clearTimeout(timeout);
            refreshChannel?.removeEventListener('message', onMessage);
            const newToken = event.data.token;
            localStorage.setItem('token', newToken);
            setToken(newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            resolve(true);
          } else if (event.data?.type === 'LOGOUT') {
            clearTimeout(timeout);
            refreshChannel?.removeEventListener('message', onMessage);
            resolve(false);
          }
        };

        if (refreshChannel) {
          refreshChannel.addEventListener('message', onMessage);
        } else {
          // No BroadcastChannel — rely on storage event (fires in other tabs)
          const onStorage = (e) => {
            if (e.key === 'token' && e.newValue && !isTokenExpired(e.newValue)) {
              clearTimeout(timeout);
              window.removeEventListener('storage', onStorage);
              setToken(e.newValue);
              axios.defaults.headers.common['Authorization'] = `Bearer ${e.newValue}`;
              resolve(true);
            }
          };
          window.addEventListener('storage', onStorage);
        }
      });
    }

    // This tab holds the lock — do the actual refresh
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const newToken = response.data.token;
      broadcastNewToken(newToken);
      setToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return true;
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 401 && status !== 403) {
        // Transient failure — rate limit (429), server error, network blip.
        // The refresh cookie is still valid, so keep the session; a later
        // attempt recovers it. Destroying the session here is what used to
        // log the whole office out whenever the shared IP hit the limiter.
        return 'transient';
      }
      // Refresh failed — session is truly over, clear everything
      emitSessionEnding();
      broadcastLogout();
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      // Clear the admin acting-team (ACTING_TEAM_KEY in constants/qaTeams.js) so
      // a stale 'kick'/'hosts' scope can't 403 the next user in this tab.
      try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
      setToken(null);
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
      return false;
    } finally {
      releaseRefreshLock();
    }
  };

  useEffect(() => {
    if (token) {
      // If access token is expired on load, try to refresh it silently
      if (isTokenExpired(token)) {
        proactiveRefresh().then(result => {
          // 'transient' is a failure too for this load path — without a fresh
          // token we can't fetch the profile, so stop the loading spinner.
          if (result !== true) setLoading(false);
        });
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Proactive refresh: refresh the access token 1 minute before it expires.
  // Random jitter (0-5s) prevents multiple tabs from firing at the exact same instant.
  useEffect(() => {
    if (!token) return;
    const payload = decodeTokenPayload(token);
    if (!payload?.exp) return;

    const expiresAt = payload.exp * 1000;
    const refreshAt = expiresAt - 60000; // 1 minute before expiry
    const jitter = Math.floor(Math.random() * 5000); // 0-5s random offset per tab
    const delay = refreshAt - Date.now() + jitter;

    if (delay <= 0) return; // Already past refresh window, interceptor will handle it

    let retryTimer = null;
    const timer = setTimeout(() => {
      proactiveRefresh().then((result) => {
        if (result === 'transient') {
          // Rate limit / blip — try once more before the token actually
          // expires; after that the 401 interceptor keeps retrying on demand.
          retryTimer = setTimeout(() => proactiveRefresh(), 30000);
        }
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      if (retryTimer) clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Cross-tab sync: listen for token updates and logouts from other tabs.
  // This ensures that when Tab A refreshes, Tab B/C/D immediately get the new token
  // without making their own refresh calls (which would trigger reuse detection).
  useEffect(() => {
    // BroadcastChannel listener (primary — instant, same-origin)
    const onBroadcast = (event) => {
      if (event.data?.type === 'TOKEN_REFRESHED' && event.data.token) {
        const newToken = event.data.token;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      } else if (event.data?.type === 'LOGOUT') {
        emitSessionEnding(); // let this tab's open editor flush its draft too
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        // Clear the admin acting-team (ACTING_TEAM_KEY in constants/qaTeams.js) so
        // a stale 'kick'/'hosts' scope can't 403 the next user in this tab.
        try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
        if (!isRedirectingRef.current) {
          isRedirectingRef.current = true;
          window.location.href = '/login';
        }
      }
    };

    // Storage event listener (fallback — fires in OTHER tabs when localStorage changes)
    const onStorage = (e) => {
      if (e.key === 'token') {
        if (e.newValue && !isTokenExpired(e.newValue)) {
          setToken(e.newValue);
          axios.defaults.headers.common['Authorization'] = `Bearer ${e.newValue}`;
        } else if (!e.newValue) {
          // Token was removed (logout from another tab). The token is already
          // gone from localStorage here, so a beacon can't authenticate — but
          // emit anyway so listeners can persist what they can (e.g. the KYC
          // report checkpoints, which live in localStorage, not the backend).
          emitSessionEnding();
          // Clear the admin acting-team (ACTING_TEAM_KEY in constants/qaTeams.js) so
          // a stale 'kick'/'hosts' scope can't 403 the next user in this tab.
          try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
          if (!isRedirectingRef.current) {
            isRedirectingRef.current = true;
            window.location.href = '/login';
          }
        }
      }
    };

    if (refreshChannel) {
      refreshChannel.addEventListener('message', onBroadcast);
    }
    window.addEventListener('storage', onStorage);

    return () => {
      if (refreshChannel) {
        refreshChannel.removeEventListener('message', onBroadcast);
      }
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      const { token, ...userData } = response.data;
      broadcastNewToken(token);

      // Store userId for Socket.io authentication
      if (userData._id) {
        localStorage.setItem('userId', userData._id);
      }

      // A fresh login must start at the default team — drop any acting-team
      // selection (ACTING_TEAM_KEY in constants/qaTeams.js) left in this tab by
      // a previous user, so a non-admin never inherits a 'kick'/'hosts' scope.
      try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }

      setToken(token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed'
      };
    }
  };

  const register = async (name, email, password, role = 'user') => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password, role },
        { withCredentials: true }
      );
      const { token, ...userData } = response.data;
      broadcastNewToken(token);

      // Store userId for Socket.io authentication
      if (userData._id) {
        localStorage.setItem('userId', userData._id);
      }

      // Fresh session must start at the default QA team — drop any acting-team
      // left in this (per-tab) sessionStorage by a previous user. See
      // constants/qaTeams.js ACTING_TEAM_KEY. (Consistency with login().)
      try { sessionStorage.removeItem('clara_qa_acting_team'); } catch { /* sessionStorage unavailable */ }

      setToken(token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  // Wrapper for external use (exposed via context)
  const logout = (forceRedirect = false) => performLogout(forceRedirect);

  // Local-dev "view as another role" (see devRolePreview.js). A no-op unless
  // NODE_ENV is development AND the page is on localhost, so a production build
  // hands the real user straight through. Applied HERE, at the one place the
  // tree reads `user` from, so every consumer — layouts, route guards,
  // useQAManager's isQAAdmin/hasCap — agrees on the previewed role instead of
  // each growing its own override.
  //
  // isAuthenticated stays keyed on the REAL user: a preview must never be able
  // to log the session out or gate it behind a login screen.
  const previewUser = useMemo(() => applyDevPreview(user), [user]);

  const value = {
    user: previewUser,
    // The account you actually are — for the preview banner, and for anything
    // that must not be fooled by the override.
    realUser: user,
    setUser,
    token,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: previewUser?.role === 'admin',
    isDeveloper: previewUser?.role === 'developer',
    isDeveloperOrAdmin: previewUser?.role === 'admin' || previewUser?.role === 'developer'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
